import { useNavigate } from "react-router-dom";
import { PageHeader } from "../components/PageHeader";

interface AuthRequiredPageProps {
  feature: "portfolio" | "bookmarks" | "settings" | "brokerage" | "execution" | "admin" | "subscription" | "builder" | "optimizer" | "portfolio-lab";
}

const featureContent = {
  portfolio: {
    title: "Your Portfolio Awaits",
    subtitle: "Track investments and monitor performance",
    icon: "M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4",
    backgroundImage:
      "https://images.unsplash.com/photo-1642790106117-e829e14a795f?w=1920&q=80",
    benefits: [
      "Track your investments across multiple strategies",
      "Monitor real-time portfolio performance",
      "View detailed P&L breakdowns",
      "Analyze your allocation distribution",
      "Access transaction history",
    ],
  },
  bookmarks: {
    title: "Save Your Favorites",
    subtitle: "Build a collection of strategies you love",
    icon: "M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z",
    backgroundImage:
      "https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?w=1920&q=80",
    benefits: [
      "Bookmark strategies for quick access",
      "Organize your favorite trading approaches",
      "Compare strategies side by side",
      "Get notified of strategy updates",
      "Build your personal strategy library",
    ],
  },
  settings: {
    title: "Personalize Your Experience",
    subtitle: "Configure your trading preferences",
    icon: "M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z M15 12a3 3 0 11-6 0 3 3 0 016 0z",
    backgroundImage:
      "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=1920&q=80",
    benefits: [
      "Customize backtest parameters",
      "Set default trading preferences",
      "Configure cache settings",
      "Manage notification preferences",
      "Control data refresh intervals",
    ],
  },
  brokerage: {
    title: "Connect Your Broker",
    subtitle: "Link your Alpaca account for automated trading",
    icon: "M13 10V3L4 14h7v7l9-11h-7z",
    backgroundImage:
      "https://images.unsplash.com/photo-1590283603385-17ffb3a7f29f?w=1920&q=80",
    benefits: [
      "Connect your Alpaca trading account",
      "Securely store API credentials with AES-256 encryption",
      "View real-time positions and balances",
      "Execute trades automatically",
      "Monitor account performance",
    ],
  },
  execution: {
    title: "Automated Execution",
    subtitle: "Let your strategies trade for you",
    icon: "M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4",
    backgroundImage:
      "https://images.unsplash.com/photo-1642790106117-e829e14a795f?w=1920&q=80",
    benefits: [
      "Automated daily rebalancing at 3:40 PM EST",
      "View detailed execution logs",
      "Track order fills and performance",
      "Configure threshold-based trading",
      "Access execution history",
    ],
  },
  admin: {
    title: "Admin Dashboard",
    subtitle: "Manage platform and view activity logs",
    icon: "M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z",
    backgroundImage:
      "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=1920&q=80",
    benefits: [
      "View all user activity logs",
      "Monitor system events",
      "Export activity data for compliance",
      "Track authentication events",
      "Analyze platform usage patterns",
    ],
  },
  subscription: {
    title: "Manage Your Subscription",
    subtitle: "View plans, billing, and payment details",
    icon: "M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z",
    backgroundImage:
      "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=1920&q=80",
    benefits: [
      "Choose a plan that fits your trading style",
      "Manage payment methods securely",
      "View payment history and invoices",
      "Refer friends and earn credits",
      "Cancel or reactivate anytime",
    ],
  },
  builder: {
    title: "Visual Strategy Builder",
    subtitle: "Build and test strategies with drag-and-drop",
    icon: "M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z",
    backgroundImage:
      "https://images.unsplash.com/photo-1642790106117-e829e14a795f?w=1920&q=80",
    benefits: [
      "Build strategies visually with drag-and-drop",
      "Live TC-DSL dry run as you build",
      "Integrated backtesting from the builder",
      "Save and manage your custom strategies",
      "Export strategies for automated execution",
    ],
  },
  optimizer: {
    title: "Strategy Optimizer",
    subtitle: "Evolve and improve your trading strategies with AI",
    icon: "M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z",
    backgroundImage:
      "https://images.unsplash.com/photo-1642790106117-e829e14a795f?w=1920&q=80",
    benefits: [
      "Improve strategy returns with genetic evolution",
      "Multi-objective optimization for CAGR, Sharpe, and drawdown",
      "Walk-forward validation to prevent overfitting",
      "Compare candidates with delta metrics",
      "Run full backtests on optimized strategies",
    ],
  },
  "portfolio-lab": {
    title: "Portfolio Analyzer",
    subtitle: "Correlation analysis & optimised allocation across strategies",
    icon: "M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zm10 0a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zm10 0a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z",
    backgroundImage:
      "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=1920&q=80",
    benefits: [
      "Analyse correlations between multiple strategies",
      "Optimise portfolio allocation for risk-adjusted returns",
      "Choose from Max Sharpe, Max CAGR, or Min Drawdown modes",
      "Visualise strategy interdependence with a correlation heatmap",
      "Get suggested dollar allocations for your portfolio size",
    ],
  },
};

export function AuthRequiredPage({ feature }: AuthRequiredPageProps) {
  const navigate = useNavigate();
  const content = featureContent[feature];

  return (
    <div className="min-h-screen">
      <PageHeader
        title={content.title}
        subtitle={content.subtitle}
        backgroundImage={content.backgroundImage}
      />

      <div className="px-4 sm:px-6 lg:px-8 pb-8">
        <div className="max-w-2xl mx-auto">
          {/* Main Card */}
          <div className="glass-card overflow-hidden">
            {/* Icon Header */}
            <div className="px-6 py-8 text-center border-b border-surface-700/50 bg-gradient-to-b from-surface-800/50 to-transparent">
              <div className="w-20 h-20 mx-auto rounded-2xl bg-gradient-to-br from-brand-500/20 to-accent-500/20 border border-brand-500/30 flex items-center justify-center mb-4">
                <svg
                  className="w-10 h-10 text-brand-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d={content.icon}
                  />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-surface-100 mb-2">
                Unlock This Feature
              </h2>
              <p className="text-surface-400">
                Create a free account to access all features
              </p>
            </div>

            {/* Benefits */}
            <div className="px-6 py-6">
              <h3 className="text-sm font-medium text-surface-300 uppercase tracking-wider mb-4">
                What you'll get
              </h3>
              <ul className="space-y-3">
                {content.benefits.map((benefit, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <svg
                      className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <span className="text-surface-300">{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* CTA Buttons */}
            <div className="px-6 py-5 bg-surface-800/50 border-t border-surface-700/50">
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={() => navigate("/signup")}
                  className="flex-1 px-6 py-3 rounded-xl bg-gradient-to-r from-brand-500 to-brand-600 text-white font-semibold hover:from-brand-600 hover:to-brand-700 transition-all duration-200 shadow-lg shadow-brand-500/25"
                >
                  Create Free Account
                </button>
                <button
                  onClick={() => navigate("/login")}
                  className="flex-1 px-6 py-3 rounded-xl border border-surface-600 text-surface-200 font-medium hover:bg-surface-700/50 transition-all duration-200"
                >
                  Sign In
                </button>
              </div>
              <p className="text-center text-surface-500 text-sm mt-4">
                Already exploring?{" "}
                <button
                  onClick={() => navigate("/search")}
                  className="text-brand-400 hover:text-brand-300"
                >
                  Community strategies
                </button>{" "}
                or{" "}
                <button
                  onClick={() => navigate("/backtest")}
                  className="text-brand-400 hover:text-brand-300"
                >
                  run a backtest
                </button>
              </p>
            </div>
          </div>

          {/* Trust Indicators */}
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4 text-center">
            <div className="glass-card p-4">
              <div className="text-2xl font-bold text-surface-100">Free</div>
              <div className="text-xs text-surface-500">Forever</div>
            </div>
            <div className="glass-card p-4">
              <div className="text-2xl font-bold text-surface-100">10+</div>
              <div className="text-xs text-surface-500">Strategies</div>
            </div>
            <div className="glass-card p-4">
              <div className="text-2xl font-bold text-surface-100">10Y</div>
              <div className="text-xs text-surface-500">Backtest Data</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
